// ============================================================================
// EcgOfflineQueuePanel.tsx — отложенные снимки/разборы ЭКГ (офлайн-очередь).
//
// Явные состояния элемента (requirement «офлайн / в очереди / отправляется /
// ошибка / результат»):
//   • captured  — снимок снят без связи, ждёт разбора на сервере;
//   • analyzed  — разбор готов, ждёт сохранения (например, связь пропала);
//   • sending   — отправляется: фаза (разбор/загрузка/сохранение) + прогресс;
//   • error     — серверная ошибка (повтор или отмена человеком);
//   • результата в очереди уже нет — панель обновляет список и сообщает counts.
//
// Отправка — единый механизм lib/ecgOfflineQueue (single-flight, durable,
// восстановление после перезапуска — в main.tsx).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CloudOff, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { confirmDialog } from '@/lib/dialogs';
import { formatValue } from '@/lib/format';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { isEffectivelyOffline, subscribeOfflineFlag } from '@/lib/offlineFlag';
import { useToastStore } from '@/store/toastStore';
import {
    cancelEcgQueueItem,
    flushEcgStudyQueue,
    getEcgQueueProgress,
    listEcgQueue,
    onEcgQueueChange,
    onEcgQueueProgress,
    retryEcgQueueItem,
    type EcgQueueItem,
    type EcgQueueProgress,
} from '@/lib/ecgOfflineQueue';

interface Props {
    /** Вызывается после успешной отправки (обновить список «Мои разборы»). */
    onSynced?: (savedIds: string[]) => void;
    /** Элемент отменён человеком (страница снимает пометку «в очереди»). */
    onCanceled?: (id: string) => void;
}

const PHASE_LABELS: Record<EcgQueueProgress['phase'], string> = {
    analyzing: 'разбор на сервере…',
    uploading: 'загрузка снимка…',
    saving: 'сохранение разбора…',
};

function stageLabel(item: EcgQueueItem): string {
    return item.stage === 'captured' ? 'Ожидает разбора' : 'Ожидает сохранения';
}

export function EcgOfflineQueuePanel({ onSynced, onCanceled }: Props) {
    const showSuccess = useToastStore((state) => state.showSuccess);
    const showError = useToastStore((state) => state.showError);
    const [items, setItems] = useState<EcgQueueItem[]>([]);
    const [progress, setProgress] = useState<EcgQueueProgress[]>(getEcgQueueProgress());
    const [offline, setOffline] = useState(isEffectivelyOffline());
    const [flushing, setFlushing] = useState(false);
    const autoFlushed = useRef(false);

    const load = useCallback(async () => {
        try {
            setItems(await listEcgQueue());
            setProgress(getEcgQueueProgress());
        } catch (e) {
            // Очередь — локальное хранилище; сбой чтения показываем явно.
            showError(friendlyError(e));
        }
    }, [showError]);

    useEffect(() => {
        void load();
        const offQueue = onEcgQueueChange(() => {
            void load();
        });
        const offProgress = onEcgQueueProgress(() => {
            setProgress(getEcgQueueProgress());
        });
        const offFlag = subscribeOfflineFlag(() => {
            setOffline(isEffectivelyOffline());
            // Связь вернулась — пробуем отправить очередь без действий человека.
            if (!isEffectivelyOffline()) {
                void (async () => {
                    try {
                        const result = await flushEcgStudyQueue();
                        if (result.saved > 0) {
                            showSuccess(`Отправлено разборов: ${result.saved}`);
                            onSynced?.(result.savedIds);
                        }
                        await load();
                    } catch (e) {
                        logErrorToJournal(e, 'ЭКГ-разбор: отправка очереди после восстановления связи');
                    }
                })();
            }
        });
        return () => {
            offQueue();
            offProgress();
            offFlag();
        };
    }, [load, onSynced, showSuccess]);

    // Открыли страницу разбора — сразу пробуем отправить накопленное
    // (очередь могла остаться с прошлой сессии; связь уже есть).
    useEffect(() => {
        if (autoFlushed.current) return;
        autoFlushed.current = true;
        if (isEffectivelyOffline()) return;
        void (async () => {
            try {
                const result = await flushEcgStudyQueue();
                if (result.saved > 0) {
                    showSuccess(`Отправлено разборов: ${result.saved}`);
                    onSynced?.(result.savedIds);
                }
                await load();
            } catch (e) {
                logErrorToJournal(e, 'ЭКГ-разбор: автоматическая отправка очереди');
            }
        })();
    }, [load, onSynced, showSuccess]);

    const flush = useCallback(async () => {
        setFlushing(true);
        try {
            const result = await flushEcgStudyQueue();
            if (result.saved > 0) {
                showSuccess(`Отправлено разборов: ${result.saved}`);
                onSynced?.(result.savedIds);
            }
            if (result.pending > 0) {
                showError(`Осталось в очереди: ${result.pending}. Проверьте связь и повторите.`);
            }
            await load();
        } catch (e) {
            showError(friendlyError(e));
        } finally {
            setFlushing(false);
        }
    }, [load, onSynced, showError, showSuccess]);

    const cancel = useCallback(async (item: EcgQueueItem) => {
        const confirmed = await confirmDialog(
            `Снимок «${item.fileName}» и результат разбора будут удалены с этого устройства. На сервер они не отправятся.`,
            'Отменить отложенный разбор?',
        );
        if (!confirmed) return;
        try {
            await cancelEcgQueueItem(item.id);
            onCanceled?.(item.id);
            await load();
        } catch (e) {
            showError(friendlyError(e));
        }
    }, [load, onCanceled, showError]);

    const retry = useCallback(async (item: EcgQueueItem) => {
        try {
            await retryEcgQueueItem(item.id);
            await load();
        } catch (e) {
            showError(friendlyError(e));
        }
    }, [load, showError]);

    if (items.length === 0) return null;

    const captured = items.filter((item) => item.stage === 'captured').length;
    const progressById = new Map(progress.map((entry) => [entry.id, entry]));

    return (
        <section className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
                <CloudOff className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium flex-1">
                    Отложенные разборы: {items.length}
                    {captured > 0 ? ` (снимков без разбора: ${captured})` : ''}
                </span>
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() => void flush()}
                    disabled={flushing || offline}
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${flushing ? 'animate-spin' : ''}`} />
                    Отправить сейчас
                </Button>
            </div>
            <div className="text-[11px] text-amber-900">
                {offline
                    ? 'Нет связи: снимки и результаты сохранены на устройстве. Отправка начнётся автоматически при восстановлении связи.'
                    : 'Снимки и результаты сохранены на устройстве и отправятся автоматически при восстановлении связи.'}
            </div>
            <ul className="space-y-1">
                {items.map((item) => {
                    const entry = progressById.get(item.id);
                    const sending = item.state === 'sending' || !!entry;
                    return (
                        <li key={item.id} className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="flex-1 min-w-[180px] truncate" title={item.fileName}>
                                    {item.fileName}
                                </span>
                                <span
                                    className={`rounded px-1.5 py-0.5 text-[10px] ${
                                        item.state === 'error'
                                            ? 'bg-destructive/15 text-destructive'
                                            : sending
                                                ? 'bg-primary/15 text-primary'
                                                : 'bg-amber-500/20 text-amber-900'
                                    }`}
                                >
                                    {item.state === 'error' ? 'Ошибка' : sending ? 'Отправляется' : stageLabel(item)}
                                </span>
                                <span className="text-muted-foreground">
                                    {formatValue(item.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp')}
                                </span>
                                {item.attempts > 0 && (
                                    <span className="text-muted-foreground">попыток: {item.attempts}</span>
                                )}
                                {item.lastError && (
                                    <span className="text-destructive" title={item.lastError}>
                                        {item.lastError}
                                    </span>
                                )}
                                {item.state === 'error' && !sending && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 px-2 text-[11px]"
                                        onClick={() => void retry(item)}
                                    >
                                        <RefreshCw className="h-3.5 w-3.5" />
                                        Повторить
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-[11px]"
                                    onClick={() => void cancel(item)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    {sending ? 'Отменить' : 'Удалить'}
                                </Button>
                            </div>
                            {entry && (
                                <div className="flex items-center gap-2 text-[11px] text-primary">
                                    <AlertCircle className="h-3 w-3" />
                                    <span>
                                        {PHASE_LABELS[entry.phase]}
                                        {entry.percent !== null ? ` ${Math.round(entry.percent * 100)}%` : ''}
                                    </span>
                                    {entry.percent !== null && (
                                        <span className="h-1 flex-1 max-w-[220px] rounded bg-primary/20 overflow-hidden">
                                            <span
                                                className="block h-full bg-primary transition-[width]"
                                                style={{ width: `${Math.round(entry.percent * 100)}%` }}
                                            />
                                        </span>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
